// TypeViewService.js
//
// System-type view templates live under `_nodetypes/{typeKey}/views/{viewKey}`.
// They are live for every base that uses the type (no per-base provision).

import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';
import { generateKey } from 'utilities/Keys';

const NODETYPES_COLLECTION = collections.nodetypes;
const TYPE_VIEWS_SUBCOLLECTION = collections.nodeTypeViews || 'views';

function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * @param {object|null|undefined} view
 * @returns {boolean}
 */
export function isSystemTypeView(view) {
  if (!view || typeof view !== 'object') return false;
  if (view.isSystemView === true) return true;
  if (normalizeString(view.viewSource).toUpperCase() === 'SYSTEM_TYPE') return true;
  if (normalizeString(view.scope).toUpperCase() === 'SYSTEM_TYPE') return true;
  return false;
}

/**
 * @param {string} typeKey
 * @param {object} data
 * @param {string} [docId]
 * @returns {object}
 */
export function normalizeSystemTypeViewRow(typeKey, data = {}, docId = '') {
  const cleanedTypeKey = normalizeString(typeKey);
  const key = normalizeString(data?.key) || normalizeString(docId);
  return {
    ...(data || {}),
    id: key || normalizeString(docId),
    key: key || normalizeString(docId),
    nodeType: cleanedTypeKey || normalizeString(data?.nodeType),
    scope: 'SYSTEM_TYPE',
    isSystemView: true,
    viewSource: 'SYSTEM_TYPE',
    baseKey: null,
    canDelete: false,
  };
}

function typeViewsCollectionRef(typeKey) {
  return collection(db, NODETYPES_COLLECTION, normalizeString(typeKey), TYPE_VIEWS_SUBCOLLECTION);
}

function typeViewDocRef(typeKey, viewKey) {
  return doc(
    db,
    NODETYPES_COLLECTION,
    normalizeString(typeKey),
    TYPE_VIEWS_SUBCOLLECTION,
    normalizeString(viewKey),
  );
}

/**
 * Subscribe to system views for a root node type.
 *
 * @param {string} typeKey
 * @param {function(object[]): void} callback
 * @returns {function(): void}
 */
export function subscribeToTypeViews(typeKey, callback) {
  const cleanedTypeKey = normalizeString(typeKey);
  if (!cleanedTypeKey || typeof callback !== 'function') {
    if (typeof callback === 'function') callback([]);
    return () => {};
  }

  return onSnapshot(
    typeViewsCollectionRef(cleanedTypeKey),
    (snapshot) => {
      const rows = snapshot.docs.map((docSnap) => (
        normalizeSystemTypeViewRow(cleanedTypeKey, docSnap.data() || {}, docSnap.id)
      ));
      callback(rows);
    },
    (error) => {
      console.error('[TypeViewService] subscribeToTypeViews error:', error);
      callback([]);
    },
  );
}

/**
 * Create or replace a system-type view template.
 *
 * @param {string} typeKey
 * @param {object} view
 * @returns {Promise<object>}
 */
export async function saveTypeView(typeKey, view = {}) {
  const cleanedTypeKey = normalizeString(typeKey);
  if (!cleanedTypeKey) {
    throw new Error('typeKey is required to save a system view.');
  }

  const viewKey = normalizeString(view?.key) || generateKey();
  const now = Timestamp.now();
  const existingCreated = view?.dateCreated || null;
  const title = normalizeString(view?.title) || 'Untitled View';
  const type = normalizeString(view?.type || 'LIST').toUpperCase() || 'LIST';

  const payload = {
    ...view,
    key: viewKey,
    nodeType: cleanedTypeKey,
    scope: 'SYSTEM_TYPE',
    isSystemView: true,
    viewSource: 'SYSTEM_TYPE',
    baseKey: null,
    userKey: null,
    title,
    type,
    description: typeof view?.description === 'string'
      ? (view.description.trim() || null)
      : null,
    position: Number.isFinite(Number(view?.position))
      ? Number(view.position)
      : now.toMillis(),
    canDelete: false,
    catchAll: false,
    dateCreated: existingCreated || now,
    dateModified: now,
  };

  // Strip client-only merge helpers that should not be persisted twice oddly
  delete payload.id;

  await setDoc(typeViewDocRef(cleanedTypeKey, viewKey), payload, { merge: false });
  return normalizeSystemTypeViewRow(cleanedTypeKey, payload, viewKey);
}

/**
 * Partially update a system-type view (e.g. position).
 *
 * @param {string} typeKey
 * @param {string} viewKey
 * @param {object} patch
 * @returns {Promise<void>}
 */
export async function updateTypeView(typeKey, viewKey, patch = {}) {
  const cleanedTypeKey = normalizeString(typeKey);
  const cleanedViewKey = normalizeString(viewKey);
  if (!cleanedTypeKey || !cleanedViewKey || !patch) return;

  await updateDoc(typeViewDocRef(cleanedTypeKey, cleanedViewKey), {
    ...patch,
    key: cleanedViewKey,
    nodeType: cleanedTypeKey,
    scope: 'SYSTEM_TYPE',
    isSystemView: true,
    viewSource: 'SYSTEM_TYPE',
    dateModified: Timestamp.now(),
  });
}

/**
 * Delete a system-type view template.
 *
 * @param {string} typeKey
 * @param {string} viewKey
 * @returns {Promise<void>}
 */
export async function deleteTypeView(typeKey, viewKey) {
  const cleanedTypeKey = normalizeString(typeKey);
  const cleanedViewKey = normalizeString(viewKey);
  if (!cleanedTypeKey || !cleanedViewKey) return;
  await deleteDoc(typeViewDocRef(cleanedTypeKey, cleanedViewKey));
}

/**
 * Merge system views with base views.
 * Base rows with the same key override system templates (per-base customization).
 * Remaining system templates are listed ahead of base-only views.
 *
 * @param {object[]} systemViews
 * @param {object[]} baseViews
 * @returns {object[]}
 */
export function mergeSystemAndBaseViews(systemViews = [], baseViews = []) {
  const baseRows = (Array.isArray(baseViews) ? baseViews : []).filter((row) => {
    const key = normalizeString(row?.key || row?.id);
    return Boolean(key);
  });
  const baseKeys = new Set(
    baseRows.map((row) => normalizeString(row?.key || row?.id)).filter(Boolean),
  );

  const systemRows = (Array.isArray(systemViews) ? systemViews : [])
    .map((row) => normalizeSystemTypeViewRow(row?.nodeType, row, row?.key || row?.id))
    .filter((row) => {
      const key = normalizeString(row?.key || row?.id);
      return key && !baseKeys.has(key);
    });

  return [...systemRows, ...baseRows];
}
